import { Component, Input, Inject, ViewChild, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../authentication/auth.service';
import { Guid } from '../../../utility/guid';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import { MatTabNav } from '@angular/material/tabs';
import { NavigationElement } from '../navigation/nav.service';

@Component({
  selector: 'app-tab-router',
  templateUrl: './tab-router.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./tab-router.component.scss'],
  standalone: false
})
export class TabRouterComponent {
  @Input() id: string = Guid.newGuid();
  @Input() isPrimary: boolean;
  @Input() stretch = false;
  @Input() tabs: NavigationElement[];
  @Input() columnSize: number = 3;
  @Input() ignoreShadow = false;

  @ViewChild(MatTabNav) navbar!: MatTabNav;

  _routeTabSelection: string;
  _inputTabSelection: string;
  @Input() set tabSelection(value: string) {
    this._inputTabSelection = value;
  }
  get tabSelection(): string {
    return this._inputTabSelection || this._routeTabSelection;
  }

  constructor(@Inject(L10N_LOCALE) public locale: L10nLocale, private router: Router, private authService: AuthService) { }

  showTab(tab: NavigationElement): boolean {
    if (tab.permission) {
      return this.authService.isSatisfied(tab.permission);
    } else {
      return true;
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  findCurrentTab() {
    // Check if of our tabs is listed explicitly by route data
    if (this.tabSelection) {
      const tab = this.tabs.find(t => this.tabSelection.indexOf(t.name) > -1);
      if (tab) {
        return tab;
      }
    }

    // Find by url match
    const curUrlTree = this.router.parseUrl(this.router.routerState.snapshot.url);
    const tabUrlTree = this.tabs.reduce((map, tab, i) => {
      map[i] = { tab: tab, urlTree: this.router.createUrlTree([tab.route], {queryParams: tab.queryParams}) };
      return map;
    }, []);

    let curTab: NavigationElement;
    let finished = false;
    while (!curTab && !finished) {
      // whittle down the current url until a match is found
      const foundTab = tabUrlTree.find(map => this.router.serializeUrl(map.urlTree) === this.router.serializeUrl(curUrlTree));
      if (foundTab) {
        curTab = foundTab.tab
      }
      // Reduce the url until we find a match 
      else if (curUrlTree.fragment) {
        curUrlTree.fragment = undefined;
      } else if (Object.keys(curUrlTree.queryParams).length) {
        const keys = Object.keys(curUrlTree.queryParams);
        delete curUrlTree.queryParams[keys[keys.length - 1]];
      } else if (curUrlTree.root.children.primary?.segments?.length) {
        curUrlTree.root.children.primary.segments.pop();
      } else {
        finished = true;
      }
    }

    if (!curTab) {
      // Match by the whatever url is the closest match
      const measureStrings = (s1: string, s2: string) => {
        let count = 0;
        for (let i = 0; i < s1.length && i < s2.length; i++) {
          if (s1[i] === s2[i]) {
            count++;
          } else {
            break;
          }
        }
        return count;
      };
      const curUrl = this.router.routerState.snapshot.url;
      const closest = this.tabs.reduce((biggest, tab, _i) => {
        const url = this.router.serializeUrl(this.router.createUrlTree([tab.route], { queryParams: tab.queryParams }));
        const count = measureStrings(curUrl, url);
        return (count > biggest.count) ? { tab: tab, count: count } : biggest;
      }, { tab: null, count: 0 });

      curTab = closest.tab;
    }
    return curTab;
  }

  isActiveTab(tab: NavigationElement) {
    return tab === this.findCurrentTab();
  }
}
